import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiCheck, FiChevronLeft, FiChevronRight, FiEdit2, FiEye, FiX } from 'react-icons/fi';
import {
  fetchAllMeters,
  fetchFieldOfficers,
  fetchMeterById,
  fetchMetersByStatus,
  humanizeError,
  normalizeMeter,
  normalizeStatus,
  patchMeterLocationDetails,
  patchMeterOldMeterDetails,
  reviewMeterInstallation,
} from '../services/api';
import { useSearch } from '../context/SearchContext';
import { useAuth } from '../context/AuthContext';
import Dropdown from '../components/global/Dropdown';
import Button from '../components/global/Button';
import DataTable from '../components/global/DataTable';
import exportIcon from '../assets/icons/ExportIcon.svg';
import { exportMetersToExcel } from '../utils/exportMetersExcel';
import DateRangePicker from '../components/filters/DateRangePicker';
import {
  getDateFilterHint,
  meterMatchesDateRange,
} from '../utils/meterDateFilter';
import { buildPageList } from '../utils/pagination';
import './StatusMetersPage.css';
import './Dashboard.css';

const ENV_PROJECT_ID = process.env.REACT_APP_PROJECT_ID;
const PAGE_SIZE = 10;

const FILTER_BY_OPTIONS = [
  { value: '', label: 'Filter By', field: 'state', allLabel: 'All Areas' },
  { value: 'state', label: 'State', field: 'state', allLabel: 'All States' },
  { value: 'depot', label: 'Depot', field: 'depot', allLabel: 'All Depots' },
  { value: 'area', label: 'Area', field: 'area', allLabel: 'All Areas' },
  { value: 'qtr', label: 'Qtr No', field: 'qtr', allLabel: 'All Qtr No' },
];

function toFiniteProjectId(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function StatusMetersPage({
  status,
  title,
  fetchRows,
  showViewMore = false,
  installationReview = false,
}) {
  const { query: searchQuery } = useSearch();
  const { user } = useAuth();
  const [urlParams, setUrlParams] = useSearchParams();
  const requestedMeterId = urlParams.get('meterId');
  const [rows, setRows] = useState([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [filterSourceRows, setFilterSourceRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [localSearch, setLocalSearch] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [filterByField, setFilterByField] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [previewImage, setPreviewImage] = useState('');
  const [reviewComments, setReviewComments] = useState('');
  const [rejectReasonOpen, setRejectReasonOpen] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [qtrDraft, setQtrDraft] = useState('');
  const [qtrEditing, setQtrEditing] = useState(false);
  const [qtrSubmitting, setQtrSubmitting] = useState(false);
  const [qtrError, setQtrError] = useState('');
  const [stateDraft, setStateDraft] = useState('');
  const [stateEditing, setStateEditing] = useState(false);
  const [stateSubmitting, setStateSubmitting] = useState(false);
  const [stateError, setStateError] = useState('');
  const [depotDraft, setDepotDraft] = useState('');
  const [depotEditing, setDepotEditing] = useState(false);
  const [depotSubmitting, setDepotSubmitting] = useState(false);
  const [depotError, setDepotError] = useState('');
  const [areaDraft, setAreaDraft] = useState('');
  const [areaEditing, setAreaEditing] = useState(false);
  const [areaSubmitting, setAreaSubmitting] = useState(false);
  const [areaError, setAreaError] = useState('');
  const [oldMeterNumberDraft, setOldMeterNumberDraft] = useState('');
  const [oldMeterNumberEditing, setOldMeterNumberEditing] = useState(false);
  const [oldMeterNumberSubmitting, setOldMeterNumberSubmitting] = useState(false);
  const [oldMeterNumberError, setOldMeterNumberError] = useState('');
  const [oldMeterReadingDraft, setOldMeterReadingDraft] = useState('');
  const [oldMeterReadingEditing, setOldMeterReadingEditing] = useState(false);
  const [oldMeterReadingSubmitting, setOldMeterReadingSubmitting] = useState(false);
  const [oldMeterReadingError, setOldMeterReadingError] = useState('');
  const [page, setPage] = useState(1);
  const reviewInFlightRef = useRef(false);

  const clientOptions = useMemo(() => {
    const raw = user?.clients;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.map((c, i) => ({
      id: String(c.id ?? c.client_id ?? c.clientId ?? i),
      label: String(c.name ?? c.client_name ?? c.label ?? `Client ${c.id ?? i}`),
    }));
  }, [user]);

  const projectOptions = useMemo(() => {
    const raw = user?.projects;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw
      .filter((p) => {
        if (!selectedClientId) return true;
        const cid = p.client_id ?? p.clientId ?? p.client?.id;
        if (cid == null) return true;
        return String(cid) === String(selectedClientId);
      })
      .map((p, i) => ({
        id: String(p.id ?? p.project_id ?? p.projectId ?? i),
        label: String(p.name ?? p.project_name ?? p.label ?? `Project ${p.id ?? i}`),
      }));
  }, [user, selectedClientId]);

  const clientDropdownOptions = useMemo(
    () => clientOptions.map((c) => ({ value: c.id, label: c.label })),
    [clientOptions],
  );

  const projectDropdownOptions = useMemo(
    () => projectOptions.map((p) => ({ value: p.id, label: p.label })),
    [projectOptions],
  );

  const searchText = useMemo(
    () => `${searchQuery} ${localSearch}`.trim(),
    [searchQuery, localSearch],
  );
  const hasClientFilters =
    Boolean(locationFilter) || Boolean(searchText) || Boolean(dateFrom) || Boolean(dateTo);
  const dateFilterHint = useMemo(() => getDateFilterHint(status), [status]);
  const useServerPagination = !fetchRows && !hasClientFilters;

  const load = useCallback(
    async ({ silent = false, targetPage } = {}) => {
      const pageNum = targetPage ?? 1;
      if (!silent) setLoading(true);
      setError('');
      try {
        if (fetchRows) {
          const customRows = await fetchRows(status);
          setRows(customRows);
          setServerTotal(customRows.length);
          return;
        }

        const officerMap = await fetchFieldOfficers();

        if (hasClientFilters) {
          const rawMeters = await fetchAllMeters();
          const normalized = rawMeters.map((m) => normalizeMeter(m, officerMap));
          const forStatus =
            status === 'All'
              ? normalized
              : normalized.filter((row) => normalizeStatus(row.status) === status);
          setRows(forStatus);
          setServerTotal(forStatus.length);
          return;
        }

        const result = await fetchMetersByStatus(status, pageNum, PAGE_SIZE);
        const batch = (result.data || []).map((m) => normalizeMeter(m, officerMap));
        const forPage =
          status === 'All'
            ? batch
            : batch.filter((row) => normalizeStatus(row.status) === status);
        setRows(forPage);
        setServerTotal(Number(result.meta?.pagination?.total) || forPage.length);
      } catch (err) {
        setError(humanizeError(err, 'Could not load meters. Please try again.'));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [status, fetchRows, hasClientFilters],
  );

  useEffect(() => {
    load({ targetPage: useServerPagination ? page : 1 });
  }, [load, useServerPagination, page]);

  useEffect(() => {
    if (fetchRows) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [rawMeters, officerMap] = await Promise.all([
          fetchAllMeters(),
          fetchFieldOfficers(),
        ]);
        if (cancelled) return;
        const normalized = rawMeters.map((m) => normalizeMeter(m, officerMap));
        const forStatus =
          status === 'All'
            ? normalized
            : normalized.filter((row) => normalizeStatus(row.status) === status);
        setFilterSourceRows(forStatus);
      } catch {
        /* filter dropdowns are optional; table still works */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, fetchRows]);

  useEffect(() => {
    if (!selectedProjectId) return;
    if (!projectOptions.some((p) => p.id === selectedProjectId)) {
      setSelectedProjectId('');
    }
  }, [projectOptions, selectedProjectId]);

  const activeFilterMeta = useMemo(
    () => FILTER_BY_OPTIONS.find((f) => f.value === filterByField) ?? FILTER_BY_OPTIONS[0],
    [filterByField],
  );

  const locationOptions = useMemo(
    () =>
      [
        ...new Set(
          (filterSourceRows ?? rows)
            .map((r) => r[activeFilterMeta.field])
            .filter((d) => d && d !== '—'),
        ),
      ].sort(),
    [filterSourceRows, rows, activeFilterMeta.field],
  );

  const locationDropdownOptions = useMemo(
    () => [
      { value: '', label: activeFilterMeta.allLabel },
      ...locationOptions.map((d) => ({ value: d, label: d })),
    ],
    [locationOptions, activeFilterMeta.allLabel],
  );

  const filterByDropdownOptions = useMemo(
    () =>
      FILTER_BY_OPTIONS.filter((f) => f.value !== '').map((f) => ({
        value: f.value,
        label: f.label,
      })),
    [],
  );

  const applyMeterDrafts = useCallback((meter) => {
    if (!meter) return;
    setQtrDraft(meter.qtr && meter.qtr !== '—' ? String(meter.qtr) : '');
    setStateDraft(meter.state && meter.state !== '—' ? String(meter.state) : '');
    setDepotDraft(meter.depot && meter.depot !== '—' ? String(meter.depot) : '');
    setAreaDraft(meter.area && meter.area !== '—' ? String(meter.area) : '');
    setOldMeterNumberDraft(
      meter.oldMeterNumber != null && meter.oldMeterNumber !== ''
        ? String(meter.oldMeterNumber)
        : '',
    );
    setOldMeterReadingDraft(
      meter.oldMeterReading != null && meter.oldMeterReading !== ''
        ? String(meter.oldMeterReading)
        : '',
    );
  }, []);

  const openMeterDetails = useCallback((row) => {
    setReviewComments('');
    setRejectReasonOpen(false);
    setReviewError('');
    setQtrEditing(false);
    setQtrError('');
    setStateEditing(false);
    setStateError('');
    setDepotEditing(false);
    setDepotError('');
    setAreaEditing(false);
    setAreaError('');
    setOldMeterNumberEditing(false);
    setOldMeterNumberError('');
    setOldMeterReadingEditing(false);
    setOldMeterReadingError('');
    applyMeterDrafts(row);
    setSelectedMeter(row);
  }, [applyMeterDrafts]);

  const closeMeterDetails = useCallback(() => {
    setSelectedMeter(null);
    setReviewComments('');
    setRejectReasonOpen(false);
    setReviewError('');
    setQtrDraft('');
    setQtrEditing(false);
    setQtrError('');
    setQtrSubmitting(false);
    setStateDraft('');
    setStateEditing(false);
    setStateError('');
    setStateSubmitting(false);
    setDepotDraft('');
    setDepotEditing(false);
    setDepotError('');
    setDepotSubmitting(false);
    setAreaDraft('');
    setAreaEditing(false);
    setAreaError('');
    setAreaSubmitting(false);
    setOldMeterNumberDraft('');
    setOldMeterNumberEditing(false);
    setOldMeterNumberError('');
    setOldMeterNumberSubmitting(false);
    setOldMeterReadingDraft('');
    setOldMeterReadingEditing(false);
    setOldMeterReadingError('');
    setOldMeterReadingSubmitting(false);
  }, []);

  const effectiveProjectId = useMemo(
    () =>
      toFiniteProjectId(selectedMeter?.projectId) ??
      toFiniteProjectId(selectedProjectId) ??
      toFiniteProjectId(ENV_PROJECT_ID),
    [selectedMeter?.projectId, selectedProjectId],
  );

  useEffect(() => {
    if (!showViewMore || !selectedMeter?.id) return undefined;

    let cancelled = false;
    const meterId = selectedMeter.id;

    (async () => {
      try {
        const projectId =
          toFiniteProjectId(selectedMeter.projectId) ??
          toFiniteProjectId(selectedProjectId) ??
          toFiniteProjectId(ENV_PROJECT_ID);
        const [raw, officerMap] = await Promise.all([
          fetchMeterById(meterId, { projectId }),
          fetchFieldOfficers(),
        ]);
        if (cancelled || !raw) return;

        const fresh = normalizeMeter(raw, officerMap);
        if (Number(fresh.id) !== Number(meterId)) return;

        setSelectedMeter((prev) =>
          prev && Number(prev.id) === Number(meterId) ? fresh : prev,
        );
        applyMeterDrafts(fresh);
        setRows((prev) =>
          prev.map((row) => (Number(row.id) === Number(meterId) ? fresh : row)),
        );
        setFilterSourceRows((prev) => {
          if (!Array.isArray(prev)) return prev;
          return prev.map((row) => (Number(row.id) === Number(meterId) ? fresh : row));
        });
      } catch {
        /* keep table row data if detail fetch fails */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    showViewMore,
    selectedMeter?.id,
    selectedMeter?.projectId,
    selectedProjectId,
    applyMeterDrafts,
  ]);

  const handleSaveQtr = useCallback(async () => {
    if (!selectedMeter?.id) return;
    const nextQtr = String(qtrDraft ?? '').trim();
    if (!nextQtr) {
      setQtrError('Qtr No cannot be empty.');
      return;
    }
    setQtrError('');
    setQtrSubmitting(true);
    try {
      await patchMeterLocationDetails(selectedMeter.id, {
        projectId: effectiveProjectId,
        qtr: nextQtr,
      });
      setSelectedMeter((prev) => (prev ? { ...prev, qtr: nextQtr } : prev));
      setRows((prev) =>
        prev.map((row) => (Number(row.id) === Number(selectedMeter.id) ? { ...row, qtr: nextQtr } : row)),
      );
      setFilterSourceRows((prev) => {
        if (!Array.isArray(prev)) return prev;
        return prev.map((row) =>
          Number(row.id) === Number(selectedMeter.id) ? { ...row, qtr: nextQtr } : row,
        );
      });
      setQtrEditing(false);
    } catch (err) {
      setQtrError(humanizeError(err, 'Could not update Qtr No. Please try again.'));
    } finally {
      setQtrSubmitting(false);
    }
  }, [selectedMeter, qtrDraft, effectiveProjectId]);

  const handleSaveLocationPart = useCallback(
    async ({ key, draft, setSubmitting, setError, onDone }) => {
      if (!selectedMeter?.id) return;
      const nextValue = String(draft ?? '').trim();
      if (!nextValue) {
        setError('Value cannot be empty.');
        return;
      }
      setError('');
      setSubmitting(true);
      try {
        await patchMeterLocationDetails(selectedMeter.id, {
          projectId: effectiveProjectId,
          [key]: nextValue,
        });
        setSelectedMeter((prev) => (prev ? { ...prev, [key]: nextValue } : prev));
        setRows((prev) =>
          prev.map((row) =>
            Number(row.id) === Number(selectedMeter.id) ? { ...row, [key]: nextValue } : row,
          ),
        );
        setFilterSourceRows((prev) => {
          if (!Array.isArray(prev)) return prev;
          return prev.map((row) =>
            Number(row.id) === Number(selectedMeter.id) ? { ...row, [key]: nextValue } : row,
          );
        });
        onDone?.(nextValue);
      } catch (err) {
        setError(humanizeError(err, 'Could not update value. Please try again.'));
      } finally {
        setSubmitting(false);
      }
    },
    [selectedMeter, effectiveProjectId],
  );

  const handleSaveOldMeterDetail = useCallback(
    async ({ field, draft, setSubmitting, setError, onDone }) => {
      if (!selectedMeter?.id) return;

      const payload = { projectId: effectiveProjectId };

      if (field === 'oldMeterNumber') {
        const nextValue = String(draft ?? '').trim();
        if (!nextValue) {
          setError('Old meter sl. no cannot be empty.');
          return;
        }
        payload.oldMeterNumber = nextValue;
      } else if (field === 'oldMeterReading') {
        const nextValue = String(draft ?? '').trim();
        if (!nextValue) {
          setError('Old meter reading cannot be empty.');
          return;
        }
        const num = Number(nextValue);
        if (!Number.isFinite(num)) {
          setError('Old meter reading must be a valid number.');
          return;
        }
        payload.oldMeterReading = num;
      } else {
        return;
      }

      setError('');
      setSubmitting(true);
      try {
        await patchMeterOldMeterDetails(selectedMeter.id, payload);
        const updates =
          field === 'oldMeterNumber'
            ? { oldMeterNumber: payload.oldMeterNumber, isReplacement: true }
            : { oldMeterReading: payload.oldMeterReading, isReplacement: true };

        setSelectedMeter((prev) => (prev ? { ...prev, ...updates } : prev));
        setRows((prev) =>
          prev.map((row) =>
            Number(row.id) === Number(selectedMeter.id) ? { ...row, ...updates } : row,
          ),
        );
        setFilterSourceRows((prev) => {
          if (!Array.isArray(prev)) return prev;
          return prev.map((row) =>
            Number(row.id) === Number(selectedMeter.id) ? { ...row, ...updates } : row,
          );
        });
        onDone?.(updates[field]);
      } catch (err) {
        setError(humanizeError(err, 'Could not update old meter details. Please try again.'));
      } finally {
        setSubmitting(false);
      }
    },
    [selectedMeter, effectiveProjectId],
  );

  useEffect(() => {
    if (!showViewMore || !requestedMeterId) return;
    if (selectedMeter && String(selectedMeter.id) === String(requestedMeterId)) return;

    const source = filterSourceRows && filterSourceRows.length ? filterSourceRows : rows;
    if (!source || source.length === 0) return;

    const target = source.find((row) => String(row.id) === String(requestedMeterId));
    if (!target) return;

    openMeterDetails(target);
    const next = new URLSearchParams(urlParams);
    next.delete('meterId');
    setUrlParams(next, { replace: true });
  }, [
    requestedMeterId,
    selectedMeter,
    filterSourceRows,
    rows,
    showViewMore,
    openMeterDetails,
    urlParams,
    setUrlParams,
  ]);

  const submitInstallationReview = useCallback(
    async (decision) => {
      if (!selectedMeter?.id || reviewInFlightRef.current) return;

      if (!effectiveProjectId) {
        setReviewError(
          'Please select a project from the top filter before approving or rejecting this meter.',
        );
        return;
      }

      const isReject = decision === 'rejected';
      const reason = reviewComments.trim();
      if (isReject && !reason) {
        setReviewError('Please provide a reason for rejection.');
        return;
      }

      reviewInFlightRef.current = true;
      setReviewSubmitting(true);
      setReviewError('');
      try {
        await reviewMeterInstallation(selectedMeter.id, {
          decision,
          comments: isReject ? reason : '',
          projectId: effectiveProjectId,
        });
        closeMeterDetails();
        await load({ silent: true });
        if (!fetchRows) {
          const [rawMeters, officerMap] = await Promise.all([
            fetchAllMeters({ force: true }),
            fetchFieldOfficers(),
          ]);
          const normalized = rawMeters.map((m) => normalizeMeter(m, officerMap));
          const forStatus =
            status === 'All'
              ? normalized
              : normalized.filter((row) => normalizeStatus(row.status) === status);
          setFilterSourceRows(forStatus);
        }
      } catch (err) {
        console.error('[installation-review UI] submission failed', {
          meterId: selectedMeter?.id,
          projectId: effectiveProjectId,
          decision,
          message: err?.message,
          error: err,
        });
        setReviewError(humanizeError(err, 'Could not submit the review. Please try again.'));
      } finally {
        reviewInFlightRef.current = false;
        setReviewSubmitting(false);
      }
    },
    [
      selectedMeter?.id,
      effectiveProjectId,
      reviewComments,
      load,
      closeMeterDetails,
      fetchRows,
      status,
    ],
  );

  const handleRejectClick = useCallback(() => {
    if (!rejectReasonOpen) {
      setRejectReasonOpen(true);
      setReviewError('');
      return;
    }
    submitInstallationReview('rejected');
  }, [rejectReasonOpen, submitInstallationReview]);

  const cancelRejectReason = useCallback(() => {
    setRejectReasonOpen(false);
    setReviewComments('');
    setReviewError('');
  }, []);

  const filteredRows = useMemo(() => {
    if (useServerPagination) return rows;
    let out = rows;
    if (locationFilter) {
      out = out.filter((row) => row[activeFilterMeta.field] === locationFilter);
    }
    const q = searchText.toLowerCase();
    if (q) {
      out = out.filter((row) =>
        String(row.meter).toLowerCase().includes(q) ||
        String(row.qtr).toLowerCase().includes(q) ||
        String(row.area).toLowerCase().includes(q) ||
        String(row.depot).toLowerCase().includes(q) ||
        String(row.state).toLowerCase().includes(q) ||
        String(row.officer).toLowerCase().includes(q)
      );
    }
    if (dateFrom || dateTo) {
      out = out.filter((row) => meterMatchesDateRange(row, status, dateFrom, dateTo));
    }
    return out;
  }, [
    rows,
    useServerPagination,
    locationFilter,
    activeFilterMeta.field,
    searchText,
    dateFrom,
    dateTo,
    status,
  ]);

  useEffect(() => {
    setPage(1);
  }, [locationFilter, filterByField, searchQuery, localSearch, dateFrom, dateTo]);

  const totalPages = useServerPagination
    ? Math.max(1, Math.ceil(serverTotal / PAGE_SIZE))
    : Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageList = useMemo(() => buildPageList(safePage, totalPages), [safePage, totalPages]);
  const pageRows = useMemo(
    () =>
      useServerPagination
        ? rows
        : filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [useServerPagination, rows, filteredRows, safePage],
  );
  const slOffset = (safePage - 1) * PAGE_SIZE;

  const columns = useMemo(() => {
    const base = [
      {
        id: 'sl',
        header: 'Sl. No',
        cell: (_, idx) => String(slOffset + idx + 1),
      },
      {
        id: 'meter',
        header: 'Meter Number',
        tdProps: () => ({ className: 'mono' }),
        cell: (r) => r.meter,
      },
      { id: 'qtr', header: 'Qtr No', cell: (r) => r.qtr },
      { id: 'area', header: 'Area', cell: (r) => r.area },
      { id: 'depot', header: 'Depot', cell: (r) => r.depot },
      { id: 'state', header: 'State', cell: (r) => r.state },
    ];
    if (!showViewMore) return base;
    return [
      ...base,
      {
        id: 'action',
        header: 'Action',
        cell: (r) => (
          <button
            type="button"
            className="view-more-btn"
            onClick={(e) => {
              e.stopPropagation();
              openMeterDetails(r);
            }}
          >
            View More <FiEye />
          </button>
        ),
      },
    ];
  }, [showViewMore, openMeterDetails, slOffset]);

  const handleExport = useCallback(async () => {
    const baseName = String(title || status || 'meters')
      .toLowerCase()
      .replace(/\s+/g, '_');
    const dateSuffix = new Date().toISOString().slice(0, 10);

    let exportRows = filteredRows;
    if (useServerPagination) {
      const officerMap = await fetchFieldOfficers();
      const rawMeters = await fetchAllMeters();
      const normalized = rawMeters.map((m) => normalizeMeter(m, officerMap));
      exportRows =
        status === 'All'
          ? normalized
          : normalized.filter((row) => normalizeStatus(row.status) === status);
      if (dateFrom || dateTo) {
        exportRows = exportRows.filter((row) =>
          meterMatchesDateRange(row, status, dateFrom, dateTo),
        );
      }
    }

    exportMetersToExcel({
      rows: exportRows,
      sheetName: String(title || status || 'Meters').slice(0, 31),
      fileName: `${baseName}_${dateSuffix}`,
    });
  }, [filteredRows, useServerPagination, status, title, dateFrom, dateTo]);

  return (
    <div className="dashboard status-page">
      <header className="dashboard-page-header status-header-row">
        <div>
          <h2 className="dashboard-page-title">{title}</h2>
        </div>
        <div className="dashboard-header-toolbar">
          <div className="filter-dropdown-outer header-dropdown-outer">
            <Dropdown
              options={clientDropdownOptions}
              value={selectedClientId}
              onChange={(next) => {
                setSelectedClientId(next);
                setSelectedProjectId('');
              }}
              placeholder="Select client"
              ariaLabel="Select client"
              rootClassName="header-scope-dropdown"
            />
          </div>
          <div className="filter-dropdown-outer header-dropdown-outer">
            <Dropdown
              options={projectDropdownOptions}
              value={selectedProjectId}
              onChange={setSelectedProjectId}
              placeholder="Select project"
              ariaLabel="Select project"
              rootClassName="header-scope-dropdown"
            />
          </div>
          <Button type="button" className="export-meter-csv-button" onClick={handleExport}>
            <img className="export-meter-csv-button-icon" src={exportIcon} alt="" aria-hidden="true" />
            <span className="export-meter-csv-button-label">Export</span>
          </Button>
        </div>
      </header>

      <div className="status-toolbar">
        <input
          className="status-search"
          placeholder="Search by Keywords"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
        />
        <div className="status-toolbar-right dashboard-header-toolbar">
          <div className="filter-dropdown-outer header-dropdown-outer">
            <Dropdown
              options={filterByDropdownOptions}
              value={filterByField}
              onChange={(next) => {
                setFilterByField(next ?? '');
                setLocationFilter('');
              }}
              placeholder="Filter By"
              ariaLabel="Filter by"
              rootClassName="header-scope-dropdown"
            />
          </div>
          <div className="filter-dropdown-outer header-dropdown-outer">
            <Dropdown
              options={locationDropdownOptions}
              value={locationFilter}
              onChange={setLocationFilter}
              placeholder={activeFilterMeta.allLabel}
              ariaLabel={activeFilterMeta.label}
              rootClassName="header-scope-dropdown"
            />
          </div>
          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={({ dateFrom: from, dateTo: to }) => {
              setDateFrom(from);
              setDateTo(to);
            }}
            placeholder="Filter by date"
            ariaLabel={`Filter by ${dateFilterHint} date range`}
          />
        </div>
      </div>

      <div className="status-table-wrap">
        {loading ? (
          <div className="status-state">Loading...</div>
        ) : error ? (
          <div className="status-state status-error">{error}</div>
        ) : (
          <>
            <DataTable
              tableClassName="status-table"
              columns={columns}
              rows={pageRows}
              getRowKey={(row) => row.id}
              onRowClick={showViewMore ? openMeterDetails : undefined}
              emptyMessage="No records found"
              emptyCellClassName="status-empty"
              aria-label={title}
            />
            <div className="status-pagination" role="navigation" aria-label="Table pages">
              <span className="status-total-count" aria-live="polite">
                Total: {useServerPagination ? serverTotal : filteredRows.length}
              </span>
              {filteredRows.length > 0 && (
                <div className="status-pagination-controls">
                  <button
                    type="button"
                    className="status-page-arrow"
                    disabled={safePage === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    aria-label="Previous page"
                  >
                    <FiChevronLeft />
                  </button>
                  {pageList.map((entry, idx) => {
                    if (entry === 'left-ellipsis' || entry === 'right-ellipsis') {
                      return (
                        <span key={`${entry}-${idx}`} className="status-page-ellipsis">
                          …
                        </span>
                      );
                    }
                    return (
                      <button
                        key={entry}
                        type="button"
                        className={`status-page-num ${entry === safePage ? 'active' : ''}`}
                        onClick={() => setPage(entry)}
                      >
                        {entry}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    className="status-page-arrow"
                    disabled={safePage === totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    aria-label="Next page"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showViewMore && selectedMeter && (
        <div className="meter-modal-backdrop" onClick={closeMeterDetails} role="presentation">
          <div className="meter-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="meter-modal-header">
              <h3>Meter Details</h3>
              <button type="button" className="meter-modal-close" onClick={closeMeterDetails}>
                <FiX />
              </button>
            </div>

            <div className="meter-modal-body">
              <div className="meter-modal-grid">
                <section className="meter-card">
                  <h4>Consumer & Connection Info</h4>
                  <div className="meter-kv"><span>Meter Number</span><strong>{selectedMeter.meter}</strong></div>
                  <div className="meter-kv meter-kv-qtr-edit">
                    <span>Qtr No</span>
                    <div className="meter-kv-qtr-controls">
                      {qtrEditing ? (
                        <>
                          <input
                            type="text"
                            className="meter-qtr-input"
                            value={qtrDraft}
                            onChange={(e) => setQtrDraft(e.target.value)}
                            placeholder="Enter Qtr No"
                            disabled={qtrSubmitting}
                          />
                          <button
                            type="button"
                            className="meter-qtr-save-btn"
                            onClick={handleSaveQtr}
                            disabled={qtrSubmitting}
                            aria-label="Save Qtr No"
                            title="Save Qtr No"
                          >
                            {qtrSubmitting ? '...' : <FiCheck aria-hidden="true" />}
                          </button>
                          <button
                            type="button"
                            className="meter-qtr-cancel-btn"
                            onClick={() => {
                              setQtrDraft(
                                selectedMeter?.qtr && selectedMeter.qtr !== '—'
                                  ? String(selectedMeter.qtr)
                                  : '',
                              );
                              setQtrEditing(false);
                              setQtrError('');
                            }}
                            disabled={qtrSubmitting}
                            aria-label="Cancel Qtr edit"
                            title="Cancel"
                          >
                            <FiX aria-hidden="true" />
                          </button>
                        </>
                      ) : (
                        <>
                          <strong>{selectedMeter.qtr}</strong>
                          <button
                            type="button"
                            className="meter-qtr-edit-btn"
                            onClick={() => {
                              setQtrDraft(
                                selectedMeter?.qtr && selectedMeter.qtr !== '—'
                                  ? String(selectedMeter.qtr)
                                  : '',
                              );
                              setQtrEditing(true);
                              setQtrError('');
                            }}
                            aria-label="Edit Qtr No"
                            title="Edit Qtr No"
                          >
                            <FiEdit2 aria-hidden="true" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {qtrError ? <p className="meter-review-error">{qtrError}</p> : null}
                  <div className="meter-kv"><span>Type</span><strong>{selectedMeter.type || '—'}</strong></div>
                  <div className="meter-kv"><span>Phase</span><strong>{selectedMeter.phase || '—'}</strong></div>
                  <div className="meter-kv meter-kv-qtr-edit">
                    <span>Old Meter Sl. No</span>
                    <div className="meter-kv-qtr-controls">
                      {oldMeterNumberEditing ? (
                        <>
                          <input
                            type="text"
                            className="meter-qtr-input"
                            value={oldMeterNumberDraft}
                            onChange={(e) => setOldMeterNumberDraft(e.target.value)}
                            placeholder="Enter old meter sl. no"
                            disabled={oldMeterNumberSubmitting}
                          />
                          <button
                            type="button"
                            className="meter-qtr-save-btn"
                            onClick={() =>
                              handleSaveOldMeterDetail({
                                field: 'oldMeterNumber',
                                draft: oldMeterNumberDraft,
                                setSubmitting: setOldMeterNumberSubmitting,
                                setError: setOldMeterNumberError,
                                onDone: () => setOldMeterNumberEditing(false),
                              })
                            }
                            disabled={oldMeterNumberSubmitting}
                            aria-label="Save Old Meter Sl. No"
                            title="Save Old Meter Sl. No"
                          >
                            {oldMeterNumberSubmitting ? '...' : <FiCheck aria-hidden="true" />}
                          </button>
                          <button
                            type="button"
                            className="meter-qtr-cancel-btn"
                            onClick={() => {
                              setOldMeterNumberDraft(
                                selectedMeter?.oldMeterNumber != null &&
                                  selectedMeter.oldMeterNumber !== ''
                                  ? String(selectedMeter.oldMeterNumber)
                                  : '',
                              );
                              setOldMeterNumberEditing(false);
                              setOldMeterNumberError('');
                            }}
                            disabled={oldMeterNumberSubmitting}
                            aria-label="Cancel Old Meter Sl. No edit"
                            title="Cancel"
                          >
                            <FiX aria-hidden="true" />
                          </button>
                        </>
                      ) : (
                        <>
                          <strong>
                            {selectedMeter.oldMeterNumber != null &&
                            selectedMeter.oldMeterNumber !== ''
                              ? selectedMeter.oldMeterNumber
                              : 'null'}
                          </strong>
                          <button
                            type="button"
                            className="meter-qtr-edit-btn"
                            onClick={() => {
                              setOldMeterNumberDraft(
                                selectedMeter?.oldMeterNumber != null &&
                                  selectedMeter.oldMeterNumber !== ''
                                  ? String(selectedMeter.oldMeterNumber)
                                  : '',
                              );
                              setOldMeterNumberEditing(true);
                              setOldMeterNumberError('');
                            }}
                            aria-label="Edit Old Meter Sl. No"
                            title="Edit Old Meter Sl. No"
                          >
                            <FiEdit2 aria-hidden="true" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {oldMeterNumberError ? <p className="meter-review-error">{oldMeterNumberError}</p> : null}
                  <div className="meter-kv meter-kv-qtr-edit">
                    <span>Old Meter Reading</span>
                    <div className="meter-kv-qtr-controls">
                      {oldMeterReadingEditing ? (
                        <>
                          <input
                            type="number"
                            step="any"
                            className="meter-qtr-input"
                            value={oldMeterReadingDraft}
                            onChange={(e) => setOldMeterReadingDraft(e.target.value)}
                            placeholder="Enter old meter reading"
                            disabled={oldMeterReadingSubmitting}
                          />
                          <button
                            type="button"
                            className="meter-qtr-save-btn"
                            onClick={() =>
                              handleSaveOldMeterDetail({
                                field: 'oldMeterReading',
                                draft: oldMeterReadingDraft,
                                setSubmitting: setOldMeterReadingSubmitting,
                                setError: setOldMeterReadingError,
                                onDone: () => setOldMeterReadingEditing(false),
                              })
                            }
                            disabled={oldMeterReadingSubmitting}
                            aria-label="Save Old Meter Reading"
                            title="Save Old Meter Reading"
                          >
                            {oldMeterReadingSubmitting ? '...' : <FiCheck aria-hidden="true" />}
                          </button>
                          <button
                            type="button"
                            className="meter-qtr-cancel-btn"
                            onClick={() => {
                              setOldMeterReadingDraft(
                                selectedMeter?.oldMeterReading != null &&
                                  selectedMeter.oldMeterReading !== ''
                                  ? String(selectedMeter.oldMeterReading)
                                  : '',
                              );
                              setOldMeterReadingEditing(false);
                              setOldMeterReadingError('');
                            }}
                            disabled={oldMeterReadingSubmitting}
                            aria-label="Cancel Old Meter Reading edit"
                            title="Cancel"
                          >
                            <FiX aria-hidden="true" />
                          </button>
                        </>
                      ) : (
                        <>
                          <strong>
                            {selectedMeter.oldMeterReading != null &&
                            selectedMeter.oldMeterReading !== ''
                              ? selectedMeter.oldMeterReading
                              : 'null'}
                          </strong>
                          <button
                            type="button"
                            className="meter-qtr-edit-btn"
                            onClick={() => {
                              setOldMeterReadingDraft(
                                selectedMeter?.oldMeterReading != null &&
                                  selectedMeter.oldMeterReading !== ''
                                  ? String(selectedMeter.oldMeterReading)
                                  : '',
                              );
                              setOldMeterReadingEditing(true);
                              setOldMeterReadingError('');
                            }}
                            aria-label="Edit Old Meter Reading"
                            title="Edit Old Meter Reading"
                          >
                            <FiEdit2 aria-hidden="true" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {oldMeterReadingError ? <p className="meter-review-error">{oldMeterReadingError}</p> : null}
                </section>

                <section className="meter-card">
                  <h4>Location Details</h4>
                  <div className="meter-kv meter-kv-qtr-edit">
                    <span>State</span>
                    <div className="meter-kv-qtr-controls">
                      {stateEditing ? (
                        <>
                          <input
                            type="text"
                            className="meter-qtr-input"
                            value={stateDraft}
                            onChange={(e) => setStateDraft(e.target.value)}
                            placeholder="Enter State"
                            disabled={stateSubmitting}
                          />
                          <button
                            type="button"
                            className="meter-qtr-save-btn"
                            onClick={() =>
                              handleSaveLocationPart({
                                key: 'state',
                                draft: stateDraft,
                                setSubmitting: setStateSubmitting,
                                setError: setStateError,
                                onDone: () => setStateEditing(false),
                              })
                            }
                            disabled={stateSubmitting}
                            aria-label="Save State"
                            title="Save State"
                          >
                            {stateSubmitting ? '...' : <FiCheck aria-hidden="true" />}
                          </button>
                          <button
                            type="button"
                            className="meter-qtr-cancel-btn"
                            onClick={() => {
                              setStateDraft(
                                selectedMeter?.state && selectedMeter.state !== '—'
                                  ? String(selectedMeter.state)
                                  : '',
                              );
                              setStateEditing(false);
                              setStateError('');
                            }}
                            disabled={stateSubmitting}
                            aria-label="Cancel State edit"
                            title="Cancel"
                          >
                            <FiX aria-hidden="true" />
                          </button>
                        </>
                      ) : (
                        <>
                          <strong>{selectedMeter.state}</strong>
                          <button
                            type="button"
                            className="meter-qtr-edit-btn"
                            onClick={() => {
                              setStateDraft(
                                selectedMeter?.state && selectedMeter.state !== '—'
                                  ? String(selectedMeter.state)
                                  : '',
                              );
                              setStateEditing(true);
                              setStateError('');
                            }}
                            aria-label="Edit State"
                            title="Edit State"
                          >
                            <FiEdit2 aria-hidden="true" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {stateError ? <p className="meter-review-error">{stateError}</p> : null}

                  <div className="meter-kv meter-kv-qtr-edit">
                    <span>Depot</span>
                    <div className="meter-kv-qtr-controls">
                      {depotEditing ? (
                        <>
                          <input
                            type="text"
                            className="meter-qtr-input"
                            value={depotDraft}
                            onChange={(e) => setDepotDraft(e.target.value)}
                            placeholder="Enter Depot"
                            disabled={depotSubmitting}
                          />
                          <button
                            type="button"
                            className="meter-qtr-save-btn"
                            onClick={() =>
                              handleSaveLocationPart({
                                key: 'depot',
                                draft: depotDraft,
                                setSubmitting: setDepotSubmitting,
                                setError: setDepotError,
                                onDone: () => setDepotEditing(false),
                              })
                            }
                            disabled={depotSubmitting}
                            aria-label="Save Depot"
                            title="Save Depot"
                          >
                            {depotSubmitting ? '...' : <FiCheck aria-hidden="true" />}
                          </button>
                          <button
                            type="button"
                            className="meter-qtr-cancel-btn"
                            onClick={() => {
                              setDepotDraft(
                                selectedMeter?.depot && selectedMeter.depot !== '—'
                                  ? String(selectedMeter.depot)
                                  : '',
                              );
                              setDepotEditing(false);
                              setDepotError('');
                            }}
                            disabled={depotSubmitting}
                            aria-label="Cancel Depot edit"
                            title="Cancel"
                          >
                            <FiX aria-hidden="true" />
                          </button>
                        </>
                      ) : (
                        <>
                          <strong>{selectedMeter.depot}</strong>
                          <button
                            type="button"
                            className="meter-qtr-edit-btn"
                            onClick={() => {
                              setDepotDraft(
                                selectedMeter?.depot && selectedMeter.depot !== '—'
                                  ? String(selectedMeter.depot)
                                  : '',
                              );
                              setDepotEditing(true);
                              setDepotError('');
                            }}
                            aria-label="Edit Depot"
                            title="Edit Depot"
                          >
                            <FiEdit2 aria-hidden="true" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {depotError ? <p className="meter-review-error">{depotError}</p> : null}

                  <div className="meter-kv meter-kv-qtr-edit">
                    <span>Area</span>
                    <div className="meter-kv-qtr-controls">
                      {areaEditing ? (
                        <>
                          <input
                            type="text"
                            className="meter-qtr-input"
                            value={areaDraft}
                            onChange={(e) => setAreaDraft(e.target.value)}
                            placeholder="Enter Area"
                            disabled={areaSubmitting}
                          />
                          <button
                            type="button"
                            className="meter-qtr-save-btn"
                            onClick={() =>
                              handleSaveLocationPart({
                                key: 'area',
                                draft: areaDraft,
                                setSubmitting: setAreaSubmitting,
                                setError: setAreaError,
                                onDone: () => setAreaEditing(false),
                              })
                            }
                            disabled={areaSubmitting}
                            aria-label="Save Area"
                            title="Save Area"
                          >
                            {areaSubmitting ? '...' : <FiCheck aria-hidden="true" />}
                          </button>
                          <button
                            type="button"
                            className="meter-qtr-cancel-btn"
                            onClick={() => {
                              setAreaDraft(
                                selectedMeter?.area && selectedMeter.area !== '—'
                                  ? String(selectedMeter.area)
                                  : '',
                              );
                              setAreaEditing(false);
                              setAreaError('');
                            }}
                            disabled={areaSubmitting}
                            aria-label="Cancel Area edit"
                            title="Cancel"
                          >
                            <FiX aria-hidden="true" />
                          </button>
                        </>
                      ) : (
                        <>
                          <strong>{selectedMeter.area}</strong>
                          <button
                            type="button"
                            className="meter-qtr-edit-btn"
                            onClick={() => {
                              setAreaDraft(
                                selectedMeter?.area && selectedMeter.area !== '—'
                                  ? String(selectedMeter.area)
                                  : '',
                              );
                              setAreaEditing(true);
                              setAreaError('');
                            }}
                            aria-label="Edit Area"
                            title="Edit Area"
                          >
                            <FiEdit2 aria-hidden="true" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {areaError ? <p className="meter-review-error">{areaError}</p> : null}
                </section>

                <section className="meter-card">
                  <h4>Field Officer Details</h4>
                  <div className="meter-kv"><span>Name</span><strong>{selectedMeter.officer}</strong></div>
                  <div className="meter-kv"><span>Installation Date</span><strong>{selectedMeter.date}</strong></div>
                </section>

                <section className="meter-card">
                  <h4>Technical Details</h4>
                  <div className="meter-kv"><span>Status</span><strong>{selectedMeter.status}</strong></div>
                  <div className="meter-kv">
                    <span>Coordinates</span>
                    <strong>
                      {selectedMeter.latitude && selectedMeter.longitude
                        ? `Lat: ${selectedMeter.latitude}, Long: ${selectedMeter.longitude}`
                        : '—'}
                    </strong>
                  </div>
                </section>
              </div>

              <section className="meter-images-section">
                <h4>Images ({selectedMeter.photoPaths?.length || 0})</h4>
                {selectedMeter.photoPaths?.length ? (
                  <div className="meter-images-grid">
                    {selectedMeter.photoPaths.map((path, idx) => (
                      <div className="meter-image-wrap" key={`${path}-${idx}`}>
                        <span>Photo {idx + 1}</span>
                        <button
                          type="button"
                          className="meter-image-btn"
                          onClick={() => setPreviewImage(path)}
                          aria-label={`Preview photo ${idx + 1}`}
                        >
                          <div className="meter-image-thumb">
                            <img src={path} alt={`Meter ${selectedMeter.meter} ${idx + 1}`} />
                          </div>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="meter-no-images">No images available for this meter.</p>
                )}
              </section>
            </div>

            {installationReview && (
              <div className="meter-modal-footer" aria-label="Installation review">
                {(!effectiveProjectId || reviewError) && (
                  <div className="meter-modal-footer-alerts">
                    {!effectiveProjectId && (
                      <p className="meter-review-error" role="alert">
                        Select a project from the top filter to approve or reject this meter.
                      </p>
                    )}
                    {reviewError && <p className="meter-review-error" role="alert">{reviewError}</p>}
                  </div>
                )}
                {rejectReasonOpen && (
                  <div className="meter-reject-reason-row">
                    <input
                      id="installation-review-comments"
                      type="text"
                      className="meter-review-comments"
                      placeholder="Reason for rejection (required)"
                      value={reviewComments}
                      onChange={(e) => setReviewComments(e.target.value)}
                      disabled={reviewSubmitting}
                      aria-required="true"
                      autoFocus
                    />
                    <button
                      type="button"
                      className="meter-review-cancel"
                      disabled={reviewSubmitting}
                      onClick={cancelRejectReason}
                    >
                      Cancel
                    </button>
                  </div>
                )}
                <div className="meter-modal-footer-row">
                  <div className="meter-review-actions">
                    <button
                      type="button"
                      className="meter-review-btn meter-review-btn--accept"
                      disabled={reviewSubmitting || !effectiveProjectId}
                      onClick={() => submitInstallationReview('approved')}
                    >
                      {reviewSubmitting ? 'Submitting…' : 'Accept'}
                    </button>
                    <button
                      type="button"
                      className="meter-review-btn meter-review-btn--reject"
                      disabled={reviewSubmitting || !effectiveProjectId}
                      onClick={handleRejectClick}
                    >
                      {reviewSubmitting
                        ? 'Submitting…'
                        : rejectReasonOpen
                          ? 'Confirm reject'
                          : 'Reject'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showViewMore && previewImage && (
        <div className="meter-preview-backdrop" onClick={() => setPreviewImage('')} role="presentation">
          <div className="meter-preview-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="meter-preview-close"
              onClick={() => setPreviewImage('')}
              aria-label="Close image preview"
            >
              <FiX />
            </button>
            <img src={previewImage} alt="Meter preview" className="meter-preview-image" />
          </div>
        </div>
      )}
    </div>
  );
}
